import React, { useMemo } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import LinkifyIt from "linkify-it";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";

import "./RichDescriptionRenderer.scss";

const linkify = new LinkifyIt();

// Headless TipTap editor used only as a Markdown -> HTML converter so the
// editor and renderer share the exact same parsing rules.
const HEADLESS_EXTENSIONS = [
  StarterKit.configure({
    heading: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    blockquote: false,
    codeBlock: false,
    code: false,
    horizontalRule: false,
    strike: false,
    hardBreak: { keepMarks: false },
  }),
  Underline,
  Link.configure({
    openOnClick: false,
    autolink: false,
    protocols: ["http", "https", "mailto"],
    HTMLAttributes: {
      rel: "noopener noreferrer",
      target: "_blank",
    },
  }),
  Markdown.configure({
    html: true,
    tightLists: true,
    linkify: false,
    breaks: false,
  }),
];

const SANITIZER_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a"],
  ALLOWED_ATTR: ["href", "target", "rel", "title"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):)/i,
};

function ensureSafeLinks(html) {
  return html.replace(
    /<a\b([^>]*)>/gi,
    (match, attrs) => {
      const hasTarget = /\btarget\s*=/.test(attrs);
      const hasRel = /\brel\s*=/.test(attrs);
      const hasTitle = /\btitle\s*=/.test(attrs);
      const hrefMatch = attrs.match(/\bhref\s*=\s*"([^"]*)"/i);
      let next = attrs;
      if (!hasTarget) next += ' target="_blank"';
      if (!hasRel) next += ' rel="noopener noreferrer"';
      if (!hasTitle && hrefMatch) {
        // Native browser tooltip showing the destination URL on hover so
        // readers can verify where a link points before clicking it.
        const safeTitle = hrefMatch[1]
          .replace(/&/g, "&amp;")
          .replace(/"/g, "&quot;");
        next += ` title="${safeTitle}"`;
      }
      return `<a${next}>`;
    }
  );
}

/**
 * Wrap bare URLs in text nodes with clickable anchors (AC-08). Walks the
 * sanitized DOM and only touches text outside existing <a> elements so we
 * never double-wrap markdown links.
 */
function autoLinkifyDom(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      // Skip text inside existing anchors
      let parent = node.parentNode;
      while (parent && parent !== root) {
        if (parent.tagName === "A") return NodeFilter.FILTER_REJECT;
        parent = parent.parentNode;
      }
      return linkify.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const targets = [];
  let current = walker.nextNode();
  while (current) {
    targets.push(current);
    current = walker.nextNode();
  }

  targets.forEach((textNode) => {
    const matches = linkify.match(textNode.nodeValue);
    if (!matches || matches.length === 0) return;
    const frag = document.createDocumentFragment();
    let cursor = 0;
    matches.forEach((m) => {
      if (m.index > cursor) {
        frag.appendChild(document.createTextNode(textNode.nodeValue.slice(cursor, m.index)));
      }
      const a = document.createElement("a");
      a.setAttribute("href", m.url);
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      a.setAttribute("title", m.url);
      a.textContent = m.raw;
      frag.appendChild(a);
      cursor = m.lastIndex;
    });
    if (cursor < textNode.nodeValue.length) {
      frag.appendChild(document.createTextNode(textNode.nodeValue.slice(cursor)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
  });
}

export function markdownToSafeHtml(markdown) {
  if (!markdown || !String(markdown).trim()) return "";
  let html = "";
  try {
    const editor = new Editor({
      extensions: HEADLESS_EXTENSIONS,
      content: markdown,
    });
    html = editor.getHTML();
    editor.destroy();
  } catch (err) {
    // Fall back to plain text on parser crash
    return DOMPurify.sanitize(String(markdown), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }

  html = ensureSafeLinks(html);
  let sanitized = DOMPurify.sanitize(html, SANITIZER_CONFIG);
  // DOMPurify may strip target/rel on anchors as anti-tabnabbing default;
  // re-apply after sanitization so external links keep target="_blank"
  // and rel="noopener noreferrer".
  sanitized = ensureSafeLinks(sanitized);

  if (typeof document === "undefined") return sanitized;

  const container = document.createElement("div");
  container.innerHTML = sanitized;
  autoLinkifyDom(container);
  return container.innerHTML;
}

/**
 * Read-only renderer for booking rich description (RETINA-7469053).
 * - Renders markdown -> sanitized HTML.
 * - Bare URLs become clickable (AC-08).
 * - Links always open in a new tab with `rel="noopener noreferrer"`.
 */
function RichDescriptionRenderer({ value, emptyText, className, testId }) {
  const html = useMemo(() => markdownToSafeHtml(value), [value]);
  if (!html) {
    return (
      <div
        className={`rich-description-renderer rich-description-renderer--empty${
          className ? ` ${className}` : ""
        }`}
        data-testid={testId || "rich-description-renderer-empty"}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <div
      className={`rich-description-renderer${className ? ` ${className}` : ""}`}
      data-testid={testId || "rich-description-renderer"}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

RichDescriptionRenderer.propTypes = {
  value: PropTypes.string,
  emptyText: PropTypes.string,
  className: PropTypes.string,
  testId: PropTypes.string,
};

RichDescriptionRenderer.defaultProps = {
  value: "",
  emptyText: "-",
  className: undefined,
  testId: undefined,
};

export default RichDescriptionRenderer;
